import React, { Fragment } from 'react';

import Overlay from '../Overlay/Overlay';
import { Icon, Loader } from 'semantic-ui-react';
import isEqual from 'lodash/isEqual';
import { FormFieldBase } from './FormFieldBase';

const defaultOption = { text: '', value: '' };

class FormSelect extends React.Component {
  constructor(props) {
    super(props);

    const opts = Array.isArray(props.options) ? props.options : [];

    this.state = {
      selectedOption: this.getSelectedOption(props.value, opts),
      isDrawerOpen: false,
      focusIndex: 0, // update this based on props
      filterToken: '',
      filteredOptions: opts,
      lastAction: ''
    };

    this.optRef = React.createRef();
    this.inputRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { options, value } = this.props;

    if (value !== prevProps.value || !isEqual(prevProps.options, options)) {
      const opts = Array.isArray(options) ? options : [];
      const selectedOption = this.getSelectedOption(value, opts);
      this.setState({ selectedOption }, () => {
        this.filterOptions();
      });
    }
  }

  getSelectedOption = (value, options) => {
    let opts = options || this.props.options;
    opts = Array.isArray(opts) ? opts : [];
    const selectedOpt =
      options.find((opt) => opt.value === value) || defaultOption;
    return selectedOpt;
  };

  handleSelect = (e, option, index) => {
    const { createEvent, eventTypes, label } = this.props;

    if (index || index === 0) {
      this.setState({ focusIndex: index });
    }

    this.handleHideDrawer();

    if (typeof this.props.onChange === 'function') {
      // if there are no options, but select gets called, we still want to return a option shape object so that users
      // can rely on the dependable response of `(event, {value, text})` even if there are no options loaded yet
      this.props.onChange(
        e,
        typeof option === 'object' && option?.hasOwnProperty('value')
          ? option
          : defaultOption
      );
    }

    if (!!createEvent && typeof createEvent === 'function') {
      createEvent({
        action: eventTypes && eventTypes.DROPDOWN_SELECT,
        detail: `${window.location}|${label}|${option?.value}`
      });
    }
  };

  toggleDrawerVisibility = () => {
    if (!this.state.isDrawerOpen) {
      const target = this.inputRef?.current;
      if (target && target.focus && typeof target.focus === 'function') {
        target.focus();
      }
    }
    this.setState((prevState) => {
      return { ...prevState, isDrawerOpen: !prevState.isDrawerOpen };
    }, this.handleScrollOptionIntoView);
  };

  handleShowDrawer = () => {
    const { options } = this.props;
    const opts = Array.isArray(options) ? options : [];
    this.setState(
      { isDrawerOpen: true, filterOptions: opts },
      this.handleScrollOptionIntoView
    );
  };

  handleScrollOptionIntoView = () => {
    this.props.scrollIntoView &&
      this.optRef &&
      this.optRef.current &&
      this.optRef.current.scrollIntoView({
        block: 'center'
      });
  };

  handleHideDrawer = () => {
    this.setState({ isDrawerOpen: false, filterToken: '' }, () =>
      this.filterOptions()
    );
  };

  handleKeyDown = (e) => {
    const {
      focusIndex,
      isDrawerOpen,
      filteredOptions,
      lastAction
    } = this.state;
    const options = filteredOptions;

    const keymap = {
      TAB: 9,
      ARROW_DOWN: 40,
      ARROW_UP: 38,
      SPACE: 32,
      ENTER: 13
    };

    const keyCode = e.keyCode;
    const index = focusIndex;
    const last = options.length - 1;

    switch (keyCode) {
      case keymap.TAB:
        if (isDrawerOpen) {
          this.handleSelect(e, options[focusIndex]);
        }
        break;
      case keymap.ARROW_DOWN:
        if (!isDrawerOpen) {
          this.handleShowDrawer();
        } else {
          if (index < last) {
            this.setState({ focusIndex: index + 1, lastAction: 'movement' });
          } else {
            this.setState({ focusIndex: 0, lastAction: 'movement' });
          }
        }
        break;
      case keymap.ARROW_UP:
        if (!isDrawerOpen) {
          this.handleShowDrawer();
        } else {
          if (index > 0) {
            this.setState({ focusIndex: index - 1, lastAction: 'movement' });
          } else {
            this.setState({ focusIndex: last, lastAction: 'movement' });
          }
        }
        break;
      case keymap.SPACE:
        if (!isDrawerOpen) {
          this.handleShowDrawer();
        } else if (lastAction === 'movement') {
          this.handleSelect(e, options[focusIndex]);
        }
        break;
      case keymap.ENTER:
        if (!isDrawerOpen) {
          this.handleShowDrawer();
        } else {
          this.handleSelect(e, options[focusIndex]);
        }
        break;

      default:
    }
  };

  filterOptions = () => {
    const { filterToken } = this.state;
    const { options } = this.props;

    let filteredOptions = Array.isArray(options) ? options : [];

    if (filterToken !== '') {
      if (filterToken?.length === 1) {
        filteredOptions = filteredOptions.filter(
          (opt) =>
            opt?.text?.toLowerCase?.().charAt?.(0) ===
            filterToken.toLowerCase().charAt(0)
        );
      } else {
        const startsWithString = [];
        const containsString = [];
        filteredOptions.forEach((opt) => {
          const lowerOpt = opt?.text?.toLowerCase?.() || '';
          const lowerToken = filterToken?.toLowerCase?.() || '';

          if (lowerOpt.indexOf(lowerToken) === 0) {
            startsWithString.push(opt);
          } else if (lowerOpt.indexOf(lowerToken) !== -1) {
            containsString.push(opt);
          }
        });
        filteredOptions = [...startsWithString, ...containsString];
      }
    }
    this.setState({ filteredOptions });
  };

  handleChange = (e) => {
    this.setState(
      {
        filterToken: e?.target?.value || '',
        isDrawerOpen: true,
        focusIndex: 0,
        lastAction: 'typing'
      },
      () => {
        this.filterOptions();
      }
    );
  };

  render() {
    const {
      placeholder,
      searchPlaceholder,
      disabled,
      icon,
      className,
      isLoading,
      autoFocus,
      selectionFormatter,
      groupBy,
      groupSortFunction,
      scrollIntoView,
      autocomplete,
      createEvent,
      eventTypes,
      ...otherProps
    } = this.props;

    const {
      selectedOption,
      isDrawerOpen,
      focusIndex,
      filterToken,
      filteredOptions
    } = this.state;

    return (
      <FormFieldBase
        disabled={disabled}
        className={`FormSelect ${className || ''}`}
        component={
          <SelectBodyComponent
            autocomplete={autocomplete}
            inputRef={this.inputRef}
            selectionFormatter={selectionFormatter}
            filterToken={filterToken}
            placeholder={placeholder}
            searchPlaceholder={searchPlaceholder}
            selectedOption={selectedOption}
            autoFocus={autoFocus}
            icon={icon}
            isLoading={isLoading}
            isDrawerOpen={isDrawerOpen}
            focusIndex={focusIndex}
            filteredOptions={filteredOptions}
            groupBy={groupBy}
            groupSortFunction={groupSortFunction}
            scrollIntoView={scrollIntoView}
            optRef={this.optRef}
            handleShowDrawer={
              !isLoading && !disabled ? this.handleShowDrawer : () => null
            }
            handleHideDrawer={this.handleHideDrawer}
            handleChange={
              !isLoading && !disabled ? this.handleChange : () => null
            }
            handleKeyDown={
              !isLoading && !disabled ? this.handleKeyDown : () => null
            }
            handleSelect={
              !isLoading && !disabled ? this.handleSelect : () => null
            }
            toggleDrawerVisibility={
              !isLoading && !disabled ? this.toggleDrawerVisibility : () => null
            }
          />
        }
        {...otherProps}
      />
    );
  }
}

const SelectBodyComponent = ({
  autocomplete,
  handleShowDrawer,
  selectionFormatter,
  filterToken,
  placeholder,
  searchPlaceholder,
  selectedOption,
  autoFocus,
  disabled,
  handleChange,
  handleKeyDown,
  isLoading,
  isDrawerOpen,
  focusIndex,
  filteredOptions,
  handleSelect,
  handleHideDrawer,
  toggleDrawerVisibility,
  icon,
  optRef,
  groupBy,
  groupSortFunction,
  inputRef
}) => (
  <Fragment>
    {selectedOption && typeof selectedOption.text !== 'string' ? (
      <div className="selected-component" onClick={handleShowDrawer}>
        {selectedOption.text}
      </div>
    ) : typeof selectionFormatter === 'function' ? (
      <InputWithPlaceholder
        isDrawerOpen={isDrawerOpen}
        autocomplete={autocomplete}
        filterToken={filterToken}
        selectedOption={selectedOption}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        autoFocus={autoFocus}
        handleChange={handleChange}
        disabled={disabled}
        handleShowDrawer={handleShowDrawer}
        handleKeyDown={handleKeyDown}
        selectionFormatter={selectionFormatter}
        inputRef={inputRef}
      />
    ) : (
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        placeholder={
          isDrawerOpen && searchPlaceholder
            ? searchPlaceholder || placeholder
            : selectedOption?.text || placeholder
        }
        value={filterToken}
        onChange={handleChange}
        disabled={disabled}
        onClick={handleShowDrawer}
        onKeyDown={handleKeyDown}
        autoComplete={autocomplete || 'off'}
      />
    )}
    {isLoading && (
      <Loader size="tiny" style={{ margin: '8px 7px 0 0' }} inline active />
    )}
    {!isLoading && (
      <Icon
        onClick={disabled ? () => null : toggleDrawerVisibility}
        name={icon || 'caret down'}
      />
    )}
    <OptionsDrawer
      isVisible={isDrawerOpen && !disabled}
      selectedOption={selectedOption}
      focusIndex={focusIndex}
      isDrawerOpen={isDrawerOpen}
      filteredOptions={filteredOptions}
      handleSelect={handleSelect}
      handleHideDrawer={handleHideDrawer}
      optRef={optRef}
      groupBy={groupBy}
      groupSortFunction={groupSortFunction}
    />
  </Fragment>
);

const OptionsDrawer = ({
  isVisible,
  selectedOption,
  handleHideDrawer,
  isDrawerOpen,
  filteredOptions,
  handleSelect,
  focusIndex,
  optRef,
  groupBy,
  groupSortFunction
}) =>
  isVisible && (
    <Fragment>
      <Overlay
        onClick={handleHideDrawer}
        zIndex="99"
        isVisible={isDrawerOpen}
      />
      {groupBy ? (
        <GroupedOptions
          filteredOptions={filteredOptions}
          handleSelect={handleSelect}
          selectedOption={selectedOption}
          focusIndex={focusIndex}
          optRef={optRef}
          groupSortFunction={groupSortFunction}
        />
      ) : (
        <ul className="options-container">
          {!!filteredOptions &&
            filteredOptions.map((opt, i) => (
              <li
                ref={opt.value === selectedOption?.value ? optRef : null}
                key={i}
                onClick={(e) => handleSelect(e, opt, i)}
                className={`${
                  opt.value === selectedOption?.value ? 'selected' : ''
                } ${i === focusIndex ? 'focused' : ''}`}>
                {opt.text}
              </li>
            ))}
        </ul>
      )}
    </Fragment>
  );

const InputWithPlaceholder = ({
  autocomplete,
  isDrawerOpen,
  filterToken,
  selectedOption,
  placeholder,
  searchPlaceholder,
  autoFocus,
  handleChange,
  disabled,
  handleShowDrawer,
  handleKeyDown,
  selectionFormatter,
  inputRef
}) => {
  const placeholderText =
    // if nothing has been typed yet, the field is focused, and a search placeholder exists
    !filterToken && isDrawerOpen && searchPlaceholder ? (
      // then show the search placeholder
      <span className="search-placeholder">{searchPlaceholder}</span>
    ) : // else, if nothing has been typed yet and there is a selcted option
    !filterToken && selectedOption ? (
      // then show the formatted selected option
      selectionFormatter(selectedOption)
    ) : (
      // else show the placeholder text
      placeholder
    );

  return (
    <Fragment>
      <div className="simulated-placeholder">{placeholderText}</div>
      <input
        autoComplete={autocomplete || 'off'}
        ref={inputRef}
        className="with-simulated-placeholder"
        autoFocus={autoFocus}
        value={filterToken}
        onChange={handleChange}
        disabled={disabled}
        onClick={handleShowDrawer}
        onKeyDown={handleKeyDown}
      />
    </Fragment>
  );
};

const GroupedOptions = ({
  groupSortFunction,
  filteredOptions,
  handleSelect,
  selectedOption,
  focusIndex,
  optRef
}) => {
  const groups = {};

  filteredOptions.forEach((option, index) => {
    if (!option || !option.group) {
      return;
    }
    // add option index for tracking focusIndex
    // TODO: the old method for maintaining focused index seems odd... maybe replace with id's
    const opt = { ...option, optionIndex: index };

    if (groups[opt.group]) {
      groups[opt.group].push(opt);
    } else {
      groups[opt.group] = [opt];
    }
  });

  const groupNames = Object.keys(groups) || [];
  const sortedGroupNames =
    typeof groupSortFunction === 'function'
      ? groupNames.sort(groupSortFunction)
      : groupNames.sort();

  return (
    <div className="options-container">
      {sortedGroupNames.map((groupName, i) => {
        const groupItems = groups[groupName];
        return (
          <ul className="options-group" key={`groupName-${i}`}>
            <h5>{groupName}</h5>
            {!!groupItems &&
              groupItems
                .sort((a, b) =>
                  a.text > b.text ? 1 : b.text > a.text ? -1 : 0
                )
                .map((opt, ii) => (
                  <li
                    ref={opt.value === selectedOption?.value ? optRef : null}
                    key={ii}
                    onClick={(e) => handleSelect(e, opt, opt.optionIndex)}
                    className={`${
                      opt.value === selectedOption?.value ? 'selected' : ''
                    } ${opt.optionIndex === focusIndex ? 'focused' : ''}`}>
                    {opt.text}
                  </li>
                ))}
          </ul>
        );
      })}
    </div>
  );
};

export { FormSelect };
export default FormSelect;
